import type { Trace, Span, Service } from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

const OPS = [
  "GET /api/orders",
  "POST /api/orders",
  "POST /api/checkout",
  "GET /api/users/:id",
  "POST /api/auth/login",
  "GET /api/products/search",
  "PUT /api/inventory/reserve",
  "POST /api/payments/charge",
  "GET /api/notifications",
];

function makeSpans(rng: () => number, traceId: string, root: Service, all: Service[], startedAt: number, totalMs: number): Span[] {
  const spans: Span[] = [];
  const rootId = `sp_${traceId}_root`;
  spans.push({
    id: rootId, parentId: undefined, traceId,
    service: root.name,
    operation: pick(rng, OPS),
    kind: "server",
    startedAt: new Date(startedAt).toISOString(),
    durationMs: totalMs,
    status: "ok",
    attributes: { "http.method": "POST", "http.status_code": 200, "http.route": "/api/checkout" },
    events: [],
    onCriticalPath: true,
  });

  const childCount = int(rng, 3, 8);
  let cursor = startedAt + int(rng, 1, 4);
  for (let i = 0; i < childCount; i++) {
    const svc = pick(rng, all);
    const d = int(rng, 5, Math.max(6, Math.floor(totalMs * (0.6 - i * 0.05))));
    const spanId = `sp_${traceId}_${i}`;
    const status: Span["status"] = rng() < 0.06 ? "error" : "ok";
    const kind: Span["kind"] = pick(rng, ["client", "producer", "consumer", "internal"] as const);
    spans.push({
      id: spanId, parentId: rootId, traceId,
      service: svc.name,
      operation:
        kind === "client"   ? `HTTP GET /${svc.name.replace(/-.*/, "")}` :
        kind === "producer" ? `KAFKA produce ${svc.name}.events` :
        kind === "consumer" ? `KAFKA consume ${svc.name}.events` :
                              `${svc.name}.internal.compute`,
      kind,
      startedAt: new Date(cursor).toISOString(),
      durationMs: d,
      status,
      attributes: {
        "db.system": pick(rng, ["postgres", "redis", "kafka", "http"]),
        "peer.service": svc.name,
        ...(status === "error" && { "error.type": "TimeoutError", "error.message": "upstream took > 500ms" }),
      },
      events: status === "error"
        ? [{ ts: new Date(cursor + d - 1).toISOString(), name: "exception", attributes: { message: "connection reset by peer" } }]
        : [],
      onCriticalPath: i === childCount - 1 || d > totalMs * 0.4,
    });
    cursor += d + int(rng, 0, 3);
  }
  return spans;
}

export function generateTraces(services: Service[], seed = 42): { traces: Trace[]; spansByTrace: Map<string, Span[]> } {
  const rng = makeRng(seed);
  const traces: Trace[] = [];
  const spansByTrace = new Map<string, Span[]>();
  const roots = services.filter((s) => s.tags.includes("frontend") || s.tags.includes("gateway") || s.tags.includes("core"));
  for (let i = 0; i < 60; i++) {
    const traceId = `tr_${Date.now().toString(36)}_${i}`;
    const root = pick(rng, roots.length ? roots : services);
    const startedAt = Date.now() - int(rng, 60, 3600) * 1000;
    const totalMs = int(rng, 45, 1800);
    const spans = makeSpans(rng, traceId, root, services, startedAt, totalMs);
    const errorCount = spans.filter((s) => s.status === "error").length;
    traces.push({
      id: traceId,
      rootService: root.name,
      rootOperation: spans[0].operation,
      startedAt: new Date(startedAt).toISOString(),
      durationMs: totalMs,
      spanCount: spans.length,
      errorCount,
      services: [...new Set(spans.map((s) => s.service))],
      hasError: errorCount > 0,
    });
    spansByTrace.set(traceId, spans);
  }
  return { traces, spansByTrace };
}
